'use client';

import { useMemo, useState, type ReactNode } from 'react';

import { CategoryIcon } from '@/components/CategoryIcon';
import { HistoryDrawer } from '@/components/recurring-payments/HistoryDrawer';
import { RecurringStatusChip } from '@/components/recurring-payments/RecurringStatusChip';
import { useRecurringOverview } from '@/hooks/queries/useRecurringPayments';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { RecurringPayment } from '@/lib/api';
import { track, trackEvent } from '@/lib/telemetry';
import { categoryChipStyleFromColor, resolveCategoryColor } from '@/lib/transactions/categories';
import { cn } from '@/lib/utils';

import {
  buildAssinaturasCycle,
  cycleDateLabel,
  type AssinaturaCycleRow,
  type AssinaturasCycle,
} from './assinaturasCycle';
import { WidgetCard, type WidgetState } from './WidgetCard';

/** How many cycle rows the card shows; the rest roll into the "+N" line. */
const MAX_ROWS = 6;

function countLabel(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** W8 · Despesas Fixas — the cycle's subscriptions + fixed bills with
 * Prevista/Paga status + totals. */
export function AssinaturasWidget(): ReactNode {
  const { data, error, isLoading } = useRecurringOverview();
  const cycle = useMemo(() => (data ? buildAssinaturasCycle(data) : null), [data]);
  const [historyItem, setHistoryItem] = useState<RecurringPayment | null>(null);

  // Rows open the SAME history drawer as the Recorrentes page — the cycle row
  // projection keeps only the id, so look the full item back up in the overview.
  const openHistory = (rowId: string): void => {
    const item = data?.groups.flatMap((group) => group.items).find((i) => i.id === rowId) ?? null;
    if (!item) return;
    setHistoryItem(item);
    trackEvent('new_home.widget_action', { widget: 'assinaturas', action: 'open_history' });
    track('action.recurring.open_history', { source: 'home_widget' });
  };

  // A stale copy keeps rendering through background errors/revalidation.
  let state: WidgetState = 'ready';
  if (!cycle) state = isLoading || !error ? 'loading' : 'error';
  else if (cycle.rows.length === 0) state = 'empty';

  const forecast = cycle?.rows.filter((row) => row.status === 'prevista').length ?? 0;
  const onInvoice = cycle?.rows.filter((row) => row.status === 'na-fatura').length ?? 0;
  const paid = cycle?.rows.filter((row) => row.status === 'paga').length ?? 0;
  const eyebrow =
    cycle && cycle.rows.length > 0
      ? [
          'Despesas Fixas',
          countLabel(forecast, 'prevista', 'previstas'),
          onInvoice > 0 ? `${onInvoice} na fatura` : null,
          paid > 0 ? countLabel(paid, 'paga', 'pagas') : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : 'Despesas Fixas';

  return (
    <WidgetCard
      eyebrow={eyebrow}
      action={{ label: 'Recorrentes', href: '/recurring-payments' }}
      state={state}
      emptyLabel="Nenhuma despesa fixa neste ciclo"
    >
      {cycle && <AssinaturasCycleBody cycle={cycle} onOpenHistory={openHistory} />}
      <HistoryDrawer item={historyItem} onClose={() => setHistoryItem(null)} />
    </WidgetCard>
  );
}

function AssinaturasCycleBody({
  cycle,
  onOpenHistory,
}: {
  cycle: AssinaturasCycle;
  onOpenHistory: (rowId: string) => void;
}): ReactNode {
  const formatCurrency = useFormatCurrency();
  const visible = cycle.rows.slice(0, MAX_ROWS);

  return (
    <div className="flex flex-col">
      <ul className="divide-y divide-border-subtle" role="list">
        {visible.map((row) => (
          <AssinaturaRow
            key={row.id}
            row={row}
            // A settled row shows what actually left the account, not the series' forecast.
            amount={formatCurrency(row.settledAmount ?? row.amount)}
            onOpen={() => onOpenHistory(row.id)}
          />
        ))}
      </ul>
      <OverflowCount count={cycle.rows.length - visible.length} />
      {/* Aligned rows instead of a dot-separated run-on line (design review) —
          on narrow cards the values line up in a scannable column. */}
      <footer className="mt-3 border-t border-border-subtle pt-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-text-secondary">Total no ciclo</span>
          <span className="font-mono text-sm font-semibold text-text-primary tabular-nums">
            {formatCurrency(cycle.total)}
          </span>
        </div>
        {(cycle.pagos > 0 || cycle.naFatura > 0) && (
          <dl className="mt-1 space-y-0.5">
            {cycle.pagos > 0 && <TotalBreakdownRow label="Pagas" value={formatCurrency(cycle.pagos)} />}
            {cycle.naFatura > 0 && (
              <TotalBreakdownRow label="Na fatura" value={formatCurrency(cycle.naFatura)} />
            )}
            <TotalBreakdownRow label="Previstas" value={formatCurrency(cycle.previstos)} />
          </dl>
        )}
      </footer>
    </div>
  );
}

function AssinaturaRow({
  row,
  amount,
  onOpen,
}: {
  row: AssinaturaCycleRow;
  amount: string;
  onOpen: () => void;
}): ReactNode {
  const chip = categoryChipStyleFromColor(resolveCategoryColor(row.categoryKey));
  const date = cycleDateLabel(row.date);
  // Landed charges (paid OR on the fatura) read solid; forecasts stay muted.
  const paid = row.status !== 'prevista';

  return (
    <li className="first:[&>button]:pt-0 last:[&>button]:pb-0">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Ver histórico de ${row.name}`}
        className="flex w-full items-center gap-3 py-2 text-left transition-opacity hover:opacity-80"
      >
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: chip.backgroundColor }}
          aria-hidden
        >
          <CategoryIcon name={row.iconName} className="size-4" style={{ color: chip.color }} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text-primary">{row.name}</p>
          {date && <p className="truncate text-xs text-text-tertiary">{date}</p>}
        </div>
        <span
          className={cn(
            'shrink-0 font-mono text-sm tabular-nums',
            paid ? 'text-text-primary' : 'text-text-secondary',
          )}
        >
          {amount}
        </span>
        <RecurringStatusChip status={row.status} />
      </button>
    </li>
  );
}

function TotalBreakdownRow({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-xs text-text-tertiary">{label}</dt>
      <dd className="font-mono text-xs text-text-tertiary tabular-nums">{value}</dd>
    </div>
  );
}

function OverflowCount({ count }: { count: number }): ReactNode {
  if (count <= 0) return null;
  return <p className="mt-2 text-xs text-text-tertiary">+{count} no ciclo</p>;
}
